<template>
  <div class="header-rfc-nav" ref="headerRfcNav">
    <button class="media header-rfc-trigger-card header-rfc-trigger" type="button" @click.stop="toggleRfcDropdown" title="Mis RFCs">
      <div class="media-body header-rfc-trigger-content">
        <span>{{ activeFiscalNameDisplay }}</span>
        <p class="mb-0 font-roboto">
          {{ activeRfcDisplay }} <i class="middle fa fa-angle-down"></i>
        </p>
      </div>
    </button>

    <ul class="header-rfc-dropdown" v-if="showRfcDropdown" ref="rfcDropdown">
      <li class="rfc-dropdown-header">
        <span class="rfc-dropdown-title">Mis RFCs</span>
      </li>
      <li v-if="hasRfcs" class="rfc-dropdown-search-item">
        <input
          v-model="rfcSearchTerm"
          type="text"
          class="rfc-dropdown-search"
          placeholder="Buscar RFC o razon social"
          autocomplete="off"
          @click.stop
        />
      </li>
      <li v-if="hasRfcs" v-for="rfcOption in filteredRfcs" :key="rfcOption.rfc">
        <a @click.prevent="changeRFC(rfcOption)" :class="{ 'is-active-rfc': rfcOption.rfc === activeRfc }">
          <span class="rfc-option-name">{{ rfcOption.fiscal_name || rfcOption.rfc }}</span>
          <small class="rfc-option-code">{{ rfcOption.rfc }}</small>
          <em class="rfc-option-state" v-if="rfcOption.rfc === activeRfc">Activo</em>
        </a>
      </li>
      <li v-if="hasRfcs && filteredRfcs.length === 0" class="empty-rfc empty-rfc-card">
        <div class="empty-rfc-title">Sin coincidencias</div>
        <small class="empty-rfc-text">No se encontraron RFCs con ese criterio de busqueda.</small>
      </li>
      <li v-if="!hasRfcs" class="empty-rfc empty-rfc-card">
        <div class="empty-rfc-title">No hay RFCs disponibles</div>
        <br />
        <small class="empty-rfc-text">Registra tu primer RFC para comenzar a consultar y organizar tus CFDIs.</small>
        <br />
        <button v-if="showRegisterRfcAction" type="button" style="float: right;" class="btn btn-primary btn-sm empty-rfc-action" @click="goToRfcSettings">
          <i class="fa fa-plus me-1"></i>
          Registrar RFC
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import ws from '@/services/rfcs';

export default {
  name: 'RfcSelector',
  data() {
    return {
      showRfcDropdown: false,
      activeRfc: '',
      activeFiscalName: '',
      rfcs: [],
      rfcSearchTerm: '',
    };
  },
  computed: {
    sortedRfcs() {
      return [...this.rfcs].sort((a, b) => {
        const nameA = String((a && (a.fiscal_name || a.rfc)) || '').trim();
        const nameB = String((b && (b.fiscal_name || b.rfc)) || '').trim();

        return nameA.localeCompare(nameB, 'es', { sensitivity: 'base', numeric: true });
      });
    },
    filteredRfcs() {
      const term = String(this.rfcSearchTerm || '').trim().toLowerCase();
      if (!term) {
        return this.sortedRfcs;
      }

      return this.sortedRfcs.filter((item) => {
        const rfc = String(item?.rfc || '').toLowerCase();
        const fiscalName = String(item?.fiscal_name || '').toLowerCase();

        return rfc.includes(term) || fiscalName.includes(term);
      });
    },
    hasRfcs() {
      return this.rfcs.length > 0;
    },
    activeRfcDisplay() {
      const rfc = String(this.activeRfc || '').trim();
      return rfc || 'RFC no seleccionado';
    },
    activeFiscalNameDisplay() {
      const fiscalName = String(this.activeFiscalName || '').trim();
      return fiscalName || 'Sin razón social activa';
    },
    showRegisterRfcAction() {
      const routeName = String(this.$route?.name || '');
      const section = String(this.$route?.query?.section || '').trim().toLowerCase();

      if (routeName === 'settings' && section === 'rfcs') {
        return false;
      }

      return true;
    }
  },
  methods: {
    emitHeaderDropdownOpen(source) {
      window.dispatchEvent(new CustomEvent('header-dropdown-open', { detail: { source } }));
    },
    handleHeaderDropdownOpen(event) {
      const source = event && event.detail ? event.detail.source : '';

      if (source !== 'rfc') {
        this.showRfcDropdown = false;
      }
    },
    loadRFCs() {
      this.activeRfc = String(localStorage.getItem('rfc') || '').trim();
      this.activeFiscalName = String(localStorage.getItem('fiscal_name') || '').trim();
      const raw = localStorage.getItem('rfcs');

      if (!raw || raw === 'undefined') {
        this.rfcs = [];
        return;
      }

      try {
        const parsed = JSON.parse(raw);
        const rfcs = Array.isArray(parsed) ? parsed : [];

        this.rfcs = rfcs.map((item) => {
          if (typeof item === 'string') {
            return {
              rfc: String(item).trim(),
              fiscal_name: ''
            };
          }

          if (item && typeof item === 'object') {
            return {
              rfc: String(item.rfc || item.RFC || '').trim(),
              fiscal_name: String(item.name || item.fiscal_name || item.razon_social || '').trim()
            };
          }

          return {
            rfc: '',
            fiscal_name: ''
          };
        }).filter((item) => item.rfc);
      } catch (_) {
        this.rfcs = [];
      }
    },
    handleRfcsStorageUpdated() {
      this.loadRFCs();
    },
    toggleRfcDropdown() {
      if (this.showRfcDropdown) {
        this.closeRfcDropdown();
        return;
      }

      this.loadRFCs();
      this.rfcSearchTerm = '';
      this.showRfcDropdown = true;
      this.emitHeaderDropdownOpen('rfc');
    },
    closeRfcDropdown(event = null) {
      if (event) {
        const dropdown = this.$refs.rfcDropdown;
        const trigger = this.$el.querySelector('.header-rfc-trigger');
        const target = event.target;

        if ((dropdown && dropdown.contains(target)) || (trigger && trigger.contains(target))) {
          return;
        }
      }

      this.rfcSearchTerm = '';
      this.showRfcDropdown = false;
    },
    async changeRFC(rfcOption) {
      const rfc = typeof rfcOption === 'string' ? rfcOption : rfcOption.rfc;
      const response = await ws.getRFC(rfc);

      if (response.type === 'success') {
        const data = response.data;
        localStorage.setItem('rfc', data.rfc);
        localStorage.setItem('fiscal_name', data.name);
        this.activeRfc = data.rfc;
        this.activeFiscalName = data.name;
        this.showRfcDropdown = false;
        this.$emit('changed', data);
        window.location.href = '/';
      } else {
        this.$toast.error(response.message);
      }
    },
    goToRfcSettings() {
      this.showRfcDropdown = false;
      this.$router.push({ name: 'settings', query: { section: 'rfcs' } });
    }
  },
  mounted() {
    document.addEventListener('click', this.closeRfcDropdown);
    window.addEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.addEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    this.loadRFCs();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeRfcDropdown);
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
  }
};
</script>

<style scoped>
.header-rfc-nav {
  position: relative;
  display: flex;
  align-items: center;
}

.header-rfc-trigger-card {
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  background: var(--white);
  padding: 8px 15px 7px;
  height: 52px;
  min-height: 52px;
  min-width: 0;
  width: auto;
  display: inline-flex;
  align-items: center;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.header-rfc-trigger-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px var(--sidebar-border);
}

.header-rfc-trigger-content {
  text-align: right;
  padding-top: 5px !important;
}

.header-rfc-trigger-content > span {
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.15;
  color: var(--theme-font-color);
}

.header-rfc-trigger-content p {
  margin: 2px 0 0 0;
  font-size: 11px;
  color: var(--theme-font-color);
  opacity: 0.82;
}

.header-rfc-trigger-content .middle {
  margin-left: 4px;
}

.header-rfc-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: auto;
  min-width: 400px;
  max-height: 460px;
  overflow-y: auto;
  margin: 0;
  padding: 0 0 8px 0;
  list-style: none;
  background-color: var(--white);
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  box-shadow: 0 2px 8px var(--sidebar-border);
  z-index: 20;
  transform-origin: top left;
  animation: headerDropdownIn 0.14s ease-out;
}

.header-rfc-dropdown .rfc-dropdown-header {
  margin: 0;
  padding: 10px 12px;
  border-bottom: 1px solid var(--sidebar-border);
  background: linear-gradient(100deg, rgba(247, 49, 100, 0.06) 0%, rgba(115, 102, 255, 0.08) 100%);
}

.header-rfc-dropdown .rfc-dropdown-title {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--theme-deafult);
  letter-spacing: 0.02em;
}

.header-rfc-dropdown .rfc-dropdown-search-item {
  margin: 0;
  padding: 8px 10px;
  border-bottom: 1px solid var(--sidebar-border);
  background-color: var(--white);
}

.header-rfc-dropdown .rfc-dropdown-search {
  width: 100%;
  border: 1px solid var(--sidebar-border);
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 12px;
  color: var(--theme-font-color);
  background: var(--white);
  outline: none;
}

.header-rfc-dropdown .rfc-dropdown-search:focus {
  border-color: var(--theme-deafult);
  box-shadow: 0 0 0 2px rgba(115, 102, 255, 0.12);
}

.header-rfc-dropdown li a,
.header-rfc-dropdown li.empty-rfc {
  display: block;
  padding: 9px 12px;
  font-size: 12px;
  color: var(--theme-font-color);
  text-decoration: none;
  position: relative;
}

.header-rfc-dropdown li a:hover {
  background-color: var(--sidebar-border);
}

.header-rfc-dropdown li + li a {
  border-top: 1px solid var(--sidebar-border);
}

.header-rfc-dropdown li a.is-active-rfc {
  background-color: var(--sidebar-border);
}

.header-rfc-dropdown li a.is-active-rfc::before {
  content: "";
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 2px;
  border-radius: 0 2px 2px 0;
  background: linear-gradient(180deg, var(--theme-secondary) 0%, var(--theme-deafult) 100%);
}

.header-rfc-dropdown li a .rfc-option-name {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--theme-font-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-rfc-dropdown li a .rfc-option-code {
  display: block;
  font-size: 11px;
  color: var(--theme-font-color);
  opacity: 0.75;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-rfc-dropdown li a .rfc-option-state {
  display: inline-block;
  margin-top: 4px;
  font-style: normal;
  font-size: 10px;
  font-weight: 600;
  color: var(--theme-deafult);
}

.empty-rfc-card {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.empty-rfc-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a2a44;
}

.empty-rfc-text {
  display: block;
  color: #6b7280;
  line-height: 1.4;
}

.empty-rfc-action {
  margin-top: 2px;
}

@keyframes headerDropdownIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
